import {
  Badge,
  Button,
  Container,
  EmptyState,
  Flex,
  Heading,
  Table,
  VStack,
} from "@chakra-ui/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { FiSearch } from "react-icons/fi"
import { z } from "zod"

import { Providers as ProvidersClient } from "@/client"
import EditProvider from "@/components/Providers/EditProvider"
import DeleteProvider from "@/components/Providers/DeleteProvider"
import AddProvider from "@/components/Providers/AddProvider"
import PendingProviders from "@/components/Providers/PendingProviders"
import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot,
} from "@/components/ui/pagination.tsx"
import { requireSuperUser } from "@/utils/routeGuards"

import type { UserPublic } from "@/client"

const providersSearchSchema = z.object({
  page: z.number().catch(1),
})

const PER_PAGE = 5

function getProvidersQueryOptions({ page }: { page: number }) {
  return {
    queryFn: () =>
      ProvidersClient.readProviders({ query: { skip: (page - 1) * PER_PAGE, limit: PER_PAGE } }),
    queryKey: ["providers", { page }],
  }
}

export const Route = createFileRoute("/_layout/providers")({
  beforeLoad: requireSuperUser(),
  component: Providers,
  validateSearch: (search) => providersSearchSchema.parse(search),
})

function ProvidersTable() {
  const navigate = useNavigate({ from: Route.fullPath })
  const { page } = Route.useSearch()

  const { data, isLoading, isPlaceholderData } = useQuery({
    ...getProvidersQueryOptions({ page }),
    placeholderData: (prevData) => prevData,
  })

  const setPage = (page: number) =>
    navigate({
      search: (prev: { page: number }) => ({ ...prev, page }),
    })

  const providers = data?.data.slice(0, PER_PAGE) ?? []
  const count = data?.count ?? 0

  const setSelectedProvider = (providerId: string, providerName?: string | null, shouldReload = false) => {
    sessionStorage.setItem("selectedProviderId", providerId)
    if (providerName) {
      sessionStorage.setItem("selectedProviderName", providerName)
    }

    if (shouldReload) {
      window.location.reload()
    }
  }

  if (isLoading) {
    return <PendingProviders />
  }

  if (providers.length === 0) {
    return (
      <EmptyState.Root>
        <EmptyState.Content>
          <EmptyState.Indicator>
            <FiSearch />
          </EmptyState.Indicator>
          <VStack textAlign="center">
            <EmptyState.Title>You don't have any providers yet</EmptyState.Title>
            <EmptyState.Description>
              Add a new provider to get started
            </EmptyState.Description>
          </VStack>
        </EmptyState.Content>
      </EmptyState.Root>
    )
  }

  return (
    <>
      <Table.Root size="sm" variant="outline">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>ABN</Table.ColumnHeader>
            <Table.ColumnHeader>Entity Name</Table.ColumnHeader>
            <Table.ColumnHeader w="120px" />
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {providers?.map((provider) => (
            <Table.Row key={provider.id} opacity={isPlaceholderData ? 0.5 : 1}>
              <Table.Cell>
                {provider.ABN ? (
                  <Badge colorPalette="gray" variant="outline" fontFamily="mono" size="sm">{provider.ABN}</Badge>
                ) : (
                  <span style={{ color: "#A0AEC0" }}>—</span>
                )}
              </Table.Cell>
              <Table.Cell fontWeight="medium">
                {provider.entityName || <span style={{ color: "#A0AEC0" }}>—</span>}
              </Table.Cell>
              <Table.Cell>
                <Flex gap={1} align="center">
                  <EditProvider provider={provider} iconOnly />
                  <DeleteProvider id={provider.id} iconOnly />
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={() => setSelectedProvider(provider.id, provider.entityName, true)}
                  >
                    Select
                  </Button>
                </Flex>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
      <Flex justifyContent="flex-end" mt={4}>
        <PaginationRoot
          count={count}
          pageSize={PER_PAGE}
          onPageChange={({ page }) => setPage(page)}
        >
          <Flex>
            <PaginationPrevTrigger />
            <PaginationItems />
            <PaginationNextTrigger />
          </Flex>
        </PaginationRoot>
      </Flex>
    </>
  )
}

function Providers() {
  const queryClient = useQueryClient()
  const currentUser = queryClient.getQueryData<UserPublic>(["currentUser"])
  return (
    <Container maxW="full">
      <Heading size="lg" pt={12}>
        Providers Management
      </Heading>
      {currentUser?.is_superuser && <AddProvider />}
      <ProvidersTable />
    </Container>
  )
}
